import { GetCandlesBulkResponseV2, GetCandlesRequestV2, GetCandlesResponseV2 } from '../../proto/generated/tick-data'
import { filterDuplicateCandles } from '../../util/chartUtils'
import {
    GetSeriesRequest,
    GetSeriesResponse,
    GetSeriesResponse_Series
} from '../../proto/generated/candle-data-depot'
import orderBookActions from '../../actions/orderBook.action'
import { OrderBook, Security, SecuritySnapshot, SecuritySnapshots } from '../../proto/generated/security'
import { updateSnapshots, updateGreeks } from '../../reducer/security.reducer'
import { Exchange, exchangeToJSON } from '../../proto/generated/common'
import api from './axiosClient'
import store, { AppDispatch } from '../../store'
import { getExchangeAndCodeFromKey, getSecuritiesFromSecuritiesObj, getSecurityKey } from '../../util/security'
import { GET_ORDER_BOOK } from '../../assets/constants'
import { Candle } from '../../proto/generated/candle-data-depot-common'
import { AxiosResponse } from 'axios'

/** Axios dedupes in-flight requests for some paths and throws { isLocal, promise } instead of rejecting. */
const awaitApiResponse = async <T>(request: Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> => {
    try {
        return await request
    } catch (error: any) {
        if (error?.isLocal && error?.promise) {
            return await error.promise
        }
        throw error
    }
}

const processSecuritySnapshotsBulkResponse = async (
    response: AxiosResponse<ArrayBuffer>,
    args: { dispatch: AppDispatch }
): Promise<SecuritySnapshots> => {
    const result = SecuritySnapshots.decode(new Uint8Array(response.data))
    const securities: Security[] = result.securitySnapshots.map((v: SecuritySnapshot) => v.referencedSecurity ? [v.security!, v.referencedSecurity] : [v.security!]).flat()
    const keys = new Set<string>(securities.map((sec) => getSecurityKey(sec.exchange, sec.code)))
    args.dispatch(updateSnapshots(result.securitySnapshots))

    const securityStore = store.getState().securityStore
    const underlyingSecuritiesToFetch: Array<{ exchange: Exchange, code: string }> = []
    const promises: Promise<any>[] = []
    result.securitySnapshots.forEach((securitySnapshot) => {
        promises.push(new Promise((resolve, reject) => {
            getSecuritiesFromSecuritiesObj(securitySnapshot.security ? [securitySnapshot.security] : []).then((sec) => resolve(sec)).catch((err) => reject(err))
        }))
    })
    const secs = (await Promise.all(promises)).flat().filter((sec): sec is Security => !!sec)
    secs.filter((sec) => !sec.hasFo).forEach((sec) => {
        const underlyingSec = getSecurityKey(sec!.exchange, sec!.underlyingSecurityCode ?? '')
        if (!keys.has(underlyingSec) && !securityStore.snapshots[underlyingSec]) {
            const [exchange, code] = getExchangeAndCodeFromKey(underlyingSec)
            underlyingSecuritiesToFetch.push({ exchange, code })
        }
    })
    if (underlyingSecuritiesToFetch.length) {
        fetchSecuritySnapshotsBulk({ securities: underlyingSecuritiesToFetch, dispatch: args.dispatch })
    }

    return result
}

export const getCandles = async (args: { body: GetCandlesRequestV2 }): Promise<GetCandlesResponseV2> => {
    return new Promise((resolve, reject) => {
        const url = `/candles`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        })
            .then(async (response) => {
                const candlesResponse = GetCandlesResponseV2.decode(new Uint8Array(response.data))
                let filteredForwardCandles = filterDuplicateCandles(candlesResponse.forwardCandles)
                if (args.body.referenceTime) {
                    filteredForwardCandles = filteredForwardCandles.filter((candle) => (candle.startTimestamp >= args.body.referenceTime))
                }
                let filteredBackwardCandles = filterDuplicateCandles(candlesResponse.backwardCandles)
                if (args.body.referenceTime) {
                    filteredBackwardCandles = filteredBackwardCandles.filter((candle) => (candle.startTimestamp <= args.body.referenceTime))
                }
                resolve({
                    ...candlesResponse,
                    forwardCandles: filteredForwardCandles as Candle[],
                    backwardCandles: filteredBackwardCandles as Candle[]
                })
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const getOrderBook = async (args: {
    security: Partial<Security>,
    dispatch: AppDispatch
}): Promise<OrderBook> => {
    return new Promise((resolve, reject) => {
        api.post(GET_ORDER_BOOK, JSON.stringify({
            exchange: args.security.exchange,
            code: args.security.code
        }), {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        })
            .then(async (response) => {
                const result = OrderBook.decode(new Uint8Array(response.data))
                orderBookActions.dispatchOrderBook(args.dispatch, result)
                resolve(result)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const fetchSecuritySnapshot = async (args: {
    security: Partial<Security>,
    dispatch: AppDispatch,
    actionType?: string
}): Promise<SecuritySnapshot> => {
    const url = `/tickdata/security-snapshot`
    const response = await awaitApiResponse(api.post(url, JSON.stringify(args.security), {
        headers: {
            Accept: 'application/x-protobuf'
        },
        responseType: 'arraybuffer'
    }))
    const result = SecuritySnapshot.decode(new Uint8Array(response.data))
    args.dispatch(updateSnapshots([result]))
    const securityStore = store.getState().securityStore
    const [sec] = result.security ? (await getSecuritiesFromSecuritiesObj([result.security])) ?? [undefined] : [undefined]
    if (sec && !sec.hasFo) {
        const underlyingSec = getSecurityKey(sec!.exchange, sec!.underlyingSecurityCode ?? '')
        if (!securityStore.snapshots[underlyingSec]) {
            const [exchange, code] = getExchangeAndCodeFromKey(underlyingSec)
            fetchSecuritySnapshot({ security: { exchange, code }, dispatch: args.dispatch })
        }
    }

    return result
}

export const fetchSecuritySnapshotsBulk = async (args: {
    securities: Array<{ exchange: Exchange, code: string }>,
    dispatch: AppDispatch
}): Promise<SecuritySnapshots> => {
    const url = `/tickdata/security-snapshots`
    const securities = args.securities.filter((s) => (s.code) && (s.exchange && s.exchange > 0))
    if (securities.length === 0) {
        return SecuritySnapshots.create({ securitySnapshots: [] })
    }

    const response = await awaitApiResponse(api.post(url, JSON.stringify({
        securities: securities.map((v) => ({
            exchange: exchangeToJSON(v.exchange),
            code: v.code
        }))
    }), {
        headers: {
            Accept: 'application/x-protobuf'
        },
        responseType: 'arraybuffer'
    }))

    return processSecuritySnapshotsBulkResponse(response, args)
}

export const getCandlesBulkV2 = async (requests: GetCandlesRequestV2[]): Promise<GetCandlesBulkResponseV2> => {
    const url = `/candles/bulk`
    const responses: Record<string, GetCandlesResponseV2> = {}
    let newRequests: GetCandlesRequestV2[] = requests

    while (true) {
        const requestMap: Record<string, GetCandlesRequestV2> = requests.reduce((acc, v) => {
            if (!v.security) return acc
            const key = getSecurityKey(v.security?.exchange, v.security.code)
            acc[key] = v

            responses[key] = {
                security: v.security,
                timeBucket: v.timeBucket,
                backwardCandles: [],
                forwardCandles: [],
                referencedSecurities: [],
                corporateActions: [],
                lastCandleStoreUpdateTime: 0,
                companyEvents: [],
                newsArticles: []
            }

            return acc
        }, {} as Record<string, GetCandlesRequestV2>)

        const apiRequests = newRequests.map((req) => ({
            ...req,
            security: {
                exchange: req.security?.exchange,
                code: req.security?.code
            }
        }))
        const response = await api.post(url, JSON.stringify({ requests: apiRequests }), {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        })
        const candleData = GetCandlesBulkResponseV2.decode(new Uint8Array(response.data))
        newRequests = []
        candleData.responses.forEach((response: GetCandlesResponseV2) => {
            if (!response.security) return
            const key = getSecurityKey(response.security?.exchange, response.security.code)
            const request = requestMap[key]
            if (request.backwardCandleCount > 0 && response.backwardCandles.length > 0 && response.backwardCandles.length < request.backwardCandleCount) {
                newRequests.push({
                    ...request,
                    backwardCandleCount: request.backwardCandleCount - response.backwardCandles.length,
                    referenceTime: response.backwardCandles[0].startTimestamp - 1
                })
            }

            if (request.forwardCandleCount > 0 && response.forwardCandles.length > 0 && response.forwardCandles.length < request.forwardCandleCount) {
                newRequests.push({
                    ...request,
                    forwardCandleCount: request.forwardCandleCount - response.forwardCandles.length,
                    referenceTime: response.forwardCandles[response.forwardCandles.length - 1].startTimestamp + 1
                })
            }
            responses[key] = {
                ...responses[key],
                backwardCandles: [...(filterDuplicateCandles(response.backwardCandles) as Candle[]), ...responses[key].backwardCandles],
                forwardCandles: [...responses[key].forwardCandles, ...(filterDuplicateCandles(response.forwardCandles) as Candle[])],
                referencedSecurities: [...responses[key].referencedSecurities, ...response.referencedSecurities],
                corporateActions: [...responses[key].corporateActions, ...response.corporateActions],
                companyEvents: response.companyEvents.map((event) => ({
                    ...event,
                    date: new Date(event.date).getTime() as unknown as string
                })),
                lastCandleStoreUpdateTime: response.lastCandleStoreUpdateTime / 1000,
                newsArticles: [...responses[key].newsArticles, ...response.newsArticles]
            }
        })
        newRequests = []
        if (newRequests.length === 0) break
    }

    return {
        responses: Object.values(responses)
    }
}

export const fetchGreeksBulk = async (args: {
    securities: Array<{ exchange: Exchange, code: string }>,
    dispatch: AppDispatch
}): Promise<void> => {
    const securities = args.securities.filter((s) => s.code)
    
    if (securities.length === 0) {
        return Promise.resolve()
    }
    
    try {
        const candleRequests: GetCandlesRequestV2[] = securities.map(security => ({
            security: {
                exchange: security.exchange,
                code: security.code
            },
            timeBucket: {
                timeSeries: 1,
                value: 1
            },
            backwardCandleCount: 1,
            forwardCandleCount: 0,
            referenceTime: Date.now(),
            dontGiveExtraCandles: true,
            dontApplyCorporateActionAdjustments: true
        }))
        
        const response = await getCandlesBulkV2(candleRequests)
        const greeksData: {securityKey: string, greeks: any}[] = []
        response.responses.forEach((candleResponse) => {
            if (!candleResponse.security) return
            
            const securityKey = getSecurityKey(candleResponse.security.exchange, candleResponse.security.code)
            const candles = candleResponse.backwardCandles
            
            if (candles.length > 0) {
                const latestCandle = candles[candles.length - 1]
                if (latestCandle.optionGreeks) {
                    greeksData.push({
                        securityKey,
                        greeks: latestCandle.optionGreeks
                    })
                }
            }
        })
        
        if (greeksData.length > 0) {
            args.dispatch(updateGreeks(greeksData))
        }
    } catch (error) {
        console.error('Error fetching Greeks:', error)
    }
}

export const getSeriesData = async (request: GetSeriesRequest): Promise<GetSeriesResponse> => {
    const url = `/series`
    return api.post(url, JSON.stringify(request), {
        headers: {
            Accept: 'application/x-protobuf'
        },
        responseType: 'arraybuffer'
    })
        .then(async (response) => {
            const result = GetSeriesResponse.decode(new Uint8Array(response.data))
            let minTimestamp = Number.MAX_VALUE
            const { series } = result
            let dataPresent = false
            series.forEach(({ firstDatapointTime, dataPoints }) => {
                minTimestamp = Math.min(minTimestamp, (new Date(firstDatapointTime)).getTime())
                dataPresent = dataPoints.length > 0
            })

            if (dataPresent && minTimestamp > request.startTime) {

                const nextResult = await getSeriesData({
                    ...request,
                    endTime: minTimestamp - 10
                })

                const existingSeriesMap: Record<string, GetSeriesResponse_Series> = series.reduce((acc: Record<string, GetSeriesResponse_Series>, series) => {
                    acc[series.seriesName] = series
                    return acc
                }, {} as Record<string, GetSeriesResponse_Series>)
                nextResult.series.forEach((sr) => {
                    if (sr.dataPoints.length) {
                        const existingSeries = { ...existingSeriesMap[sr.seriesName] }
                        const newDataPoints = [...sr.dataPoints, ...(existingSeriesMap[sr.seriesName]?.dataPoints || [])]
                        existingSeries.dataPoints = newDataPoints
                        existingSeries.firstDatapointTime = new Date(newDataPoints[0].timestamp).toISOString()
                        existingSeries.lastDatapointTime = new Date(newDataPoints[newDataPoints.length - 1].timestamp).toISOString()
                        existingSeriesMap[sr.seriesName] = existingSeries
                    }
                })
                return {
                    series: Object.values(existingSeriesMap)
                }
            }
            return result
        })
}
